import { useQuery } from '@tanstack/react-query';
import { privateApi } from '../../../api/api';

// Impor skema dan tipe yang relevan.
// Kita menggunakan kembali skema User dari GetUsers karena strukturnya kemungkinan besar sama.
import { GetUserV4Response, GetUserV4ResponseSchema } from '../schemas/GetUsersV4Schema';

const API_BASE_ENDPOINT = '/v4/user';

/**
 * Hook kustom untuk mengambil data detail satu pengguna (Versi 4).
 * 
 * @param userId - ID dari pengguna yang ingin diambil datanya. String kosong atau null akan menonaktifkan query.
 * @returns Hasil dari React Query, termasuk data pengguna, status loading, dan error.
 */
export const useGetUserV4 = (userId: string | null) => {

    return useQuery<GetUserV4Response, Error>({
        /**
         * Kunci query yang unik untuk pengguna ini.
         * Dengan menyertakan 'v4' dan 'detail', kita memisahkannya dari cache daftar pengguna.
         * Menyertakan `userId` memastikan bahwa setiap pengguna memiliki cache-nya sendiri.
         */
        queryKey: ['users', 'v4', 'detail', userId],

        /**
         * Fungsi yang akan dieksekusi oleh React Query untuk mengambil data.
         */
        queryFn: async (): Promise<GetUserV4Response> => {
            // Memastikan userId ada sebelum membuat panggilan API.
            // Meskipun 'enabled' sudah menangani ini, pengecekan ini menambah lapisan keamanan.
            if (!userId) {
                throw new Error("User ID is required to fetch user data.");
            }

            try {
                // Melakukan request GET ke endpoint spesifik pengguna.
                const response = await privateApi.get(`${API_BASE_ENDPOINT}/${userId}`);

                // Validasi struktur data respons dari API.
                const validationResult = GetUserV4ResponseSchema.safeParse(response.data);

                if (!validationResult.success) {
                    // Jika validasi gagal, log detail error dan lemparkan error baru.
                    console.error(`API response validation failed for user ID ${userId}:`, validationResult.error.flatten());
                    throw new Error("Invalid data format received from the server for single user.");
                }

                // Kembalikan data yang sudah tervalidasi.
                return validationResult.data;
            } catch (error) {
                // Tangkap dan log error dari request Axios atau dari validasi.
                console.error(`Failed to fetch user (V4) with ID ${userId}:`, error);
                // Lemparkan kembali error agar React Query dapat menanganinya.
                throw error;
            }
        },

        /**
         * Opsi 'enabled' adalah kunci untuk query yang bergantung pada suatu kondisi.
         * Query ini hanya akan berjalan jika `userId` adalah string yang "truthy" (bukan null, undefined, atau string kosong).
         * Ini mencegah panggilan API yang tidak perlu ke `/v4/user/null` saat dialog update ditutup.
         */
        enabled: !!userId,

        // Opsi tambahan untuk React Query
        refetchOnWindowFocus: false, // Biasanya data detail tidak perlu sering di-refetch.
        retry: 1, // Mencoba lagi sekali jika gagal.
    });
};